import { formatTimeRange, formatDisplayTime, formatAggregation, calculate2HourAverages, formatChartDisplayTime } from "./formatters";

export const generateEmptyDataPoints = (rawData, timeRangeType) => {
  if (!rawData || rawData.length === 0) {
    // Handle empty data
    return generateEmptyChartData(timeRangeType);
  }

  const now = new Date();
  let intervalCount, intervalType, data = [];

  switch(timeRangeType) {
    case 'today':
      const startOfDay = new Date(now);
      startOfDay.setHours(0, 0, 0, 0);
      
      // First, generate 15-minute intervals up to current time
      const fifteenMinData = [];
      
      // Calculate number of 15-minute intervals from start of day to current time
      const minutesSinceMidnight = (now.getHours() * 60) + now.getMinutes();
      const intervalsToGenerate = Math.ceil(minutesSinceMidnight / 15);
      
      for (let i = 0; i < intervalsToGenerate; i++) {
        const intervalTime = new Date(startOfDay.getTime() + i * 15 * 60000);
        const timeKey = intervalTime.getTime();
        
        // Find existing data for this time
        const existingData = rawData.find(item => 
          Math.abs(new Date(item.startTime).getTime() - timeKey) < 60000
        );
        
        if (existingData) {
          fifteenMinData.push(existingData);
        } else {
          // Create empty 15-minute data point
          const endTime = new Date(intervalTime.getTime() + 15 * 60000);
          fifteenMinData.push({
            batchId: `empty_15min_${i}`,
            intervalLabel: '15_min',
            timeRange: formatTimeRange(intervalTime.toISOString(), endTime.toISOString()),
            startTime: intervalTime.toISOString(),
            endTime: endTime.toISOString(),
            timestamp: intervalTime.getTime() / 1000,
            displayTime: formatDisplayTime(intervalTime.toISOString(), '15_min', 'today'),
            aggregation: '15 min',
            respiratory_rate: 0,
            heart_rate: 0,
            active_energy: 0,
            hrv: 0,
            dataPoints: { respiratory: 0, heart: 0, energy: 0, hrv: 0 },
            isAverage: false,
            isConsecutive: false,
            batchInfo: 'No Data',
            continuityNote: 'Missing data',
            isEmptyData: true
          });
        }
      }
      
      // Now convert to 2-hour averages
      data = calculate2HourAverages(fifteenMinData, now); // Pass current time
      break;
        
    case 'week':
      // For week: last 7 days including today
      intervalCount = 7;
      intervalType = 'daily';
      
      // Create map of existing data by date
      const weekDataMap = {};
      rawData.forEach(item => {
        const dateKey = new Date(item.startTime).toLocaleDateString('en-CA'); // YYYY-MM-DD
        if (!weekDataMap[dateKey]) {
          weekDataMap[dateKey] = {
            date: dateKey,
            respiratory_values: [],
            heart_values: [],
            energy_values: [],
            hrv_values: [],
            data: []
          };
        }
        weekDataMap[dateKey].respiratory_values.push(item.respiratory_rate || 0);
        weekDataMap[dateKey].heart_values.push(item.heart_rate || 0);
        weekDataMap[dateKey].energy_values.push(item.active_energy || 0);
        weekDataMap[dateKey].hrv_values.push(item.hrv || 0);
        weekDataMap[dateKey].data.push(item);
      });

      // Generate last 7 days
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        date.setHours(12, 0, 0, 0); // Set to midday for consistent display
        const dateKey = date.toLocaleDateString('en-CA');
        
        const existingDayData = weekDataMap[dateKey];
        if (existingDayData && existingDayData.data.length > 0) {
          // Calculate daily averages with 2 decimal places
          const respiratory_avg = parseFloat((existingDayData.respiratory_values.reduce((a, b) => a + b, 0) / existingDayData.respiratory_values.length).toFixed(2));
          const heart_avg = parseFloat((existingDayData.heart_values.reduce((a, b) => a + b, 0) / existingDayData.heart_values.length).toFixed(2));
          const energy_sum = parseFloat((existingDayData.energy_values.reduce((a, b) => a + b, 0)).toFixed(2));
          const hrv_avg = parseFloat((existingDayData.hrv_values.reduce((a, b) => a + b, 0) / existingDayData.hrv_values.length).toFixed(2));
          
          data.push({
            batchId: `week_${dateKey}`,
            intervalLabel: intervalType,
            timeRange: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
            startTime: date.toISOString(),
            endTime: date.toISOString(),
            timestamp: date.getTime() / 1000,
            displayTime: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
            aggregation: 'Daily Average',
            respiratory_rate: respiratory_avg,
            heart_rate: heart_avg,
            active_energy: energy_sum,
            hrv: hrv_avg,
            dataPoints: {
              respiratory: existingDayData.data.length,
              heart: existingDayData.data.length,
              energy: existingDayData.data.length,
              hrv: existingDayData.data.length
            },
            isAverage: true,
            isConsecutive: false,
            batchInfo: `Day ${dateKey}`,
            continuityNote: 'Daily aggregate',
            isDailyAggregate: true
          });
        } else {
          // Create empty day data
          data.push({
            batchId: `empty_day_${dateKey}`,
            intervalLabel: intervalType,
            timeRange: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
            startTime: date.toISOString(),
            endTime: date.toISOString(),
            timestamp: date.getTime() / 1000,
            displayTime: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
            aggregation: 'Daily Average',
            respiratory_rate: 0,
            heart_rate: 0,
            active_energy: 0,
            hrv: 0,
            dataPoints: { respiratory: 0, heart: 0, energy: 0, hrv: 0 },
            isAverage: true,
            isConsecutive: false,
            batchInfo: 'No Data',
            continuityNote: 'No data for this day',
            isEmptyData: true,
            isDailyAggregate: true
          });
        }
      }
      break;

    case 'month':
      // For month: last 30 days including today
      intervalCount = 30;
      intervalType = 'daily';
      
      // Create map of existing data by date
      const monthDataMap = {};
      rawData.forEach(item => {
        const dateKey = new Date(item.startTime).toLocaleDateString('en-CA');
        if (!monthDataMap[dateKey]) {
          monthDataMap[dateKey] = {
            date: dateKey,
            respiratory_values: [],
            heart_values: [],
            energy_values: [],
            hrv_values: [],
            data: []
          };
        }
        monthDataMap[dateKey].respiratory_values.push(item.respiratory_rate || 0);
        monthDataMap[dateKey].heart_values.push(item.heart_rate || 0);
        monthDataMap[dateKey].energy_values.push(item.active_energy || 0);
        monthDataMap[dateKey].hrv_values.push(item.hrv || 0);
        monthDataMap[dateKey].data.push(item);
      });

      // Generate last 30 days
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        date.setHours(12, 0, 0, 0);
        const dateKey = date.toLocaleDateString('en-CA');
        
        const existingDayData = monthDataMap[dateKey];
        if (existingDayData && existingDayData.data.length > 0) {
          // Calculate daily averages with 2 decimal places
          const respiratory_avg = parseFloat((existingDayData.respiratory_values.reduce((a, b) => a + b, 0) / existingDayData.respiratory_values.length).toFixed(2));
          const heart_avg = parseFloat((existingDayData.heart_values.reduce((a, b) => a + b, 0) / existingDayData.heart_values.length).toFixed(2));
          const energy_sum = parseFloat((existingDayData.energy_values.reduce((a, b) => a + b, 0)).toFixed(2));
          const hrv_avg = parseFloat((existingDayData.hrv_values.reduce((a, b) => a + b, 0) / existingDayData.hrv_values.length).toFixed(2));
          
          data.push({
            batchId: `month_${dateKey}`,
            intervalLabel: intervalType,
            timeRange: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            startTime: date.toISOString(),
            endTime: date.toISOString(),
            timestamp: date.getTime() / 1000,
            displayTime: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            aggregation: 'Daily Average',
            respiratory_rate: respiratory_avg,
            heart_rate: heart_avg,
            active_energy: energy_sum,
            hrv: hrv_avg,
            dataPoints: {
              respiratory: existingDayData.data.length,
              heart: existingDayData.data.length,
              energy: existingDayData.data.length,
              hrv: existingDayData.data.length
            },
            isAverage: true,
            isConsecutive: false,
            batchInfo: `Day ${dateKey}`,
            continuityNote: 'Daily aggregate',
            isDailyAggregate: true
          });
        } else {
          // Create empty day data
          data.push({
            batchId: `empty_month_${dateKey}`,
            intervalLabel: intervalType,
            timeRange: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            startTime: date.toISOString(),
            endTime: date.toISOString(),
            timestamp: date.getTime() / 1000,
            displayTime: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            aggregation: 'Daily Average',
            respiratory_rate: 0,
            heart_rate: 0,
            active_energy: 0,
            hrv: 0,
            dataPoints: { respiratory: 0, heart: 0, energy: 0, hrv: 0 },
            isAverage: true,
            isConsecutive: false,
            batchInfo: 'No Data',
            continuityNote: 'No data for this day',
            isEmptyData: true,
            isDailyAggregate: true
          });
        }
      }
      break;

    default:
      return rawData;
  }
  
  return data;
};

// New helper function for generating empty chart data
const generateEmptyChartData = (timeRangeType) => {
  const now = new Date();
  let data = [];
  
  switch(timeRangeType) {
    case 'today':
      const startOfDay = new Date(now);
      startOfDay.setHours(0, 0, 0, 0);
      const currentHour = now.getHours();
      
      // Generate 2-hour blocks that have already started
      // Determine how many 2-hour blocks have started (including current one)
      const blocksStarted = Math.floor((currentHour + 1) / 2) + 1;
      
      for (let i = 0; i < blocksStarted; i++) {
        const blockStartHour = i * 2;
        const blockStart = new Date(startOfDay);
        blockStart.setHours(blockStartHour, 0, 0, 0);
        
        const blockEnd = new Date(blockStart);
        blockEnd.setHours(blockStartHour + 2, 0, 0, 0);
        
        // Only create blocks that have started (not future blocks)
        if (blockStart.getTime() <= now.getTime()) {
          const isCurrentBlock = now.getTime() >= blockStart.getTime() && now.getTime() < blockEnd.getTime();
          
          data.push({
            batchId: `empty_2hr_${i}`,
            intervalLabel: '2_hour',
            timeRange: formatTimeRange(blockStart.toISOString(), blockEnd.toISOString()),
            startTime: blockStart.toISOString(),
            endTime: blockEnd.toISOString(),
            timestamp: blockStart.getTime() / 1000,
            displayTime: formatChartDisplayTime(blockStart.getTime(), 'today', '2_hour'),
            aggregation: '2 Hour Average',
            respiratory_rate: 0,
            heart_rate: 0,
            active_energy: 0,
            hrv: 0,
            dataPoints: { respiratory: 0, heart: 0, energy: 0, hrv: 0 },
            isAverage: true,
            isConsecutive: false,
            batchInfo: isCurrentBlock ? 'Current Time Block (No Data)' : 'No Data',
            continuityNote: 'No data available',
            isEmptyData: true,
            is2HourAggregate: true,
            isCurrentTimeBlock: isCurrentBlock
          });
        }
      }
      break;
    
    case 'week':
      // Generate last 7 days
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        date.setHours(12, 0, 0, 0);
        
        data.push({
          batchId: `empty_week_${i}`,
          intervalLabel: 'daily',
          timeRange: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
          startTime: date.toISOString(),
          endTime: date.toISOString(),
          timestamp: date.getTime() / 1000,
          displayTime: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
          aggregation: 'Daily Average',
          respiratory_rate: 0,
          heart_rate: 0,
          active_energy: 0,
          hrv: 0,
          dataPoints: { respiratory: 0, heart: 0, energy: 0, hrv: 0 },
          isAverage: true,
          isConsecutive: false,
          batchInfo: 'No Data',
          continuityNote: 'No data for this day',
          isEmptyData: true,
          isDailyAggregate: true
        });
      }
      break;
      
    case 'month':
      // Generate last 30 days
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        date.setHours(12, 0, 0, 0);
        
        data.push({
          batchId: `empty_month_${i}`,
          intervalLabel: 'daily',
          timeRange: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          startTime: date.toISOString(),
          endTime: date.toISOString(),
          timestamp: date.getTime() / 1000,
          displayTime: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          aggregation: 'Daily Average',
          respiratory_rate: 0,
          heart_rate: 0,
          active_energy: 0,
          hrv: 0,
          dataPoints: { respiratory: 0, heart: 0, energy: 0, hrv: 0 },
          isAverage: true,
          isConsecutive: false,
          batchInfo: 'No Data',
          continuityNote: 'No data for this day',
          isEmptyData: true,
          isDailyAggregate: true
        });
      }
      break;
  }
  
  return data;
};

export const processConsecutiveBatches = (metricsBatches, timeRangeType) => {
  if (!metricsBatches || metricsBatches.length === 0) return [];

  const chartData = metricsBatches.map(batch => {
    const metrics = batch.metrics;
    const isAverageData = batch.metadata?.isAverage || false;
    const originalCounts = batch.metadata?.originalDataPoints || {};

    return {
      batchId: batch.batchId,
      intervalLabel: batch.aggregation,
      timeRange: formatTimeRange(batch.batchStart, batch.batchEnd),
      startTime: batch.batchStart,
      endTime: batch.batchEnd,
      timestamp: new Date(batch.batchStart).getTime() / 1000,
      displayTime: formatDisplayTime(batch.batchStart, batch.batchEnd, batch.aggregation),
      aggregation: formatAggregation(batch.aggregation),
      
      // Directly use the stored average values
      // parseFloat(sum.toFixed(2))
      respiratory_rate: metrics.respiratory_rate || 0,
      heart_rate: metrics.heart_rate || 0,
      active_energy: metrics.active_energy || 0,
      hrv: metrics.hrv || 0,
      
      // Data points info
      dataPoints: {
        respiratory: originalCounts.respiratory_rate || 0,
        heart: originalCounts.heart_rate || 0,
        energy: originalCounts.active_energy || 0,
        hrv: originalCounts.hrv || 0
      },
      
      // Additional info
      isAverage: isAverageData,
      isConsecutive: batch.metadata?.isConsecutive || false,
      batchInfo: `Batch ${batch.batchId}`,
      continuityNote: batch.metadata?.isConsecutive ? 'Consecutive batch' : 'Gap detected'
    };
  });

  // Sort by timestamp
  const sortedData = chartData.sort((a, b) => a.timestamp - b.timestamp);
  
  // Generate empty data points based on timeRangeType
  return generateEmptyDataPoints(sortedData, timeRangeType);
};

export const calculateStatsFromBatches = (chartData) => {
  if (!chartData || chartData.length === 0) {
    return {
      respiratory: { avg: 0, min: 0, max: 0, total: 0 },
      heart: { avg: 0, min: 0, max: 0, total: 0 },
      energy: { avg: 0, min: 0, max: 0, total: 0 },
      hrv: { avg: 0, min: 0, max: 0, total: 0 }
    };
  }

  // Filter out empty data points for statistics
  const nonEmptyData = chartData.filter(item => !item.isEmptyData);
  
  // Extract values for each metric
  const respiratoryValues = nonEmptyData.map(batch => batch.respiratory_rate).filter(val => val !== null && val > 0);
  const heartValues = nonEmptyData.map(batch => batch.heart_rate).filter(val => val !== null && val > 0);
  const energyValues = nonEmptyData.map(batch => batch.active_energy).filter(val => val !== null && val > 0);
  const hrvValues = nonEmptyData.map(batch => batch.hrv).filter(val => val !== null && val > 0);

  const calculateStats = (values) => {
    if (values.length === 0) return { avg: 0, min: 0, max: 0, total: 0 };
    
    const sum = values.reduce((a, b) => a + b, 0);
    return {
      avg: parseFloat((sum / values.length).toFixed(2)),
      min: parseFloat(Math.min(...values).toFixed(2)),
      max: parseFloat(Math.max(...values).toFixed(2)),
      total: parseFloat(sum.toFixed(2))
    };
  };
  return {
    respiratory: calculateStats(respiratoryValues),
    heart: calculateStats(heartValues),
    energy: calculateStats(energyValues),
    hrv: calculateStats(hrvValues)
  };
};
